"use client";
import {
  AlertTriangle,
  Database,
  LoaderCircle,
  RefreshCw,
  ShieldAlert,
  ShieldCheck,
  Upload,
  X,
} from "lucide-react";
import {
  useRef,
  useState,
} from "react";
import {
  type ImportedIntelPack,
} from "../../../lib/intel-pack";
import {
  getThreatIntelCounts,
} from "../../../lib/threat-intel";


export function ThreatIntelView({
  installed,
  onInstall,
  onClear,
}: {
  installed: ImportedIntelPack | null;
  onInstall: (raw: string, publicKey: string) => Promise<void>;
  onClear: () => void;
}) {
  const [url, setUrl] = useState("");
  const [publicKey, setPublicKey] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const inputRef = useRef<HTMLInputElement>(null);
  const counts = getThreatIntelCounts();
  const load = async (raw: string) => {
    setLoading(true);
    setError("");
    try {
      await onInstall(raw, publicKey);
    } catch (installError) {
      setError(
        installError instanceof Error
          ? installError.message
          : "The intelligence pack could not be installed.",
      );
    } finally {
      setLoading(false);
    }
  };
  const fetchPack = async () => {
    if (!url.trim()) return;
    setLoading(true);
    setError("");
    try {
      const response = await fetch(url.trim(), {
        headers: { Accept: "application/json" },
      });
      if (!response.ok) throw new Error(`Feed returned HTTP ${response.status}.`);
      await load(await response.text());
    } catch (fetchError) {
      setError(
        fetchError instanceof Error
          ? fetchError.message
          : "The intelligence feed could not be reached.",
      );
      setLoading(false);
    }
  };
  return (
    <div className="intel-workspace">
      <input
        ref={inputRef}
        className="file-input"
        type="file"
        accept=".json,application/json"
        onChange={(event) => {
          const file = event.target.files?.[0];
          if (file) void file.text().then(load);
          event.currentTarget.value = "";
        }}
      />
      <section className="intel-hero panel">
        <div>
          <Database size={28} />
        </div>
        <span>
          <div className="section-kicker">AUDITABLE REPUTATION</div>
          <h2>{counts.total.toLocaleString()} active malware hashes</h2>
          <p>
            {counts.builtIn.toLocaleString()} built in ·{" "}
            {counts.installed.toLocaleString()} imported · SHA-256, SHA-1, and
            MD5
          </p>
          <div className="intel-family-strip">
            {counts.families.slice(0, 8).map((item) => (
              <span key={item.family}>
                {item.family}
                <strong>{item.count}</strong>
              </span>
            ))}
          </div>
        </span>
      </section>
      <div className="intel-grid">
        <section className="panel">
          <div className="panel-heading">
            <div>
              <div className="section-kicker">SIGNED UPDATE PACKS</div>
              <h3>Install threat intelligence</h3>
            </div>
          </div>
          <p className="panel-copy">
            JarLab accepts versioned JSON packs, verifies Ed25519 signatures,
            records provenance, and stores the active pack in this browser.
            Unsigned or self-asserted packs are visibly downgraded and cannot
            create a critical hash verdict.
          </p>
          <label className="intel-field">
            <span>Feed URL</span>
            <input
              value={url}
              onChange={(event) => setUrl(event.target.value)}
              placeholder="https://example.org/jarlab-intel.json"
            />
          </label>
          <label className="intel-field">
            <span>Expected Ed25519 public key</span>
            <input
              value={publicKey}
              onChange={(event) => setPublicKey(event.target.value)}
              placeholder="Base64 public key, optional"
            />
          </label>
          <div className="intel-actions">
            <button
              className="primary-button compact"
              type="button"
              onClick={() => void fetchPack()}
              disabled={loading || !url.trim()}
            >
              {loading ? (
                <LoaderCircle size={14} />
              ) : (
                <RefreshCw size={14} />
              )}
              Fetch update
            </button>
            <button
              className="secondary-button compact"
              type="button"
              onClick={() => inputRef.current?.click()}
              disabled={loading}
            >
              <Upload size={14} />
              Import JSON
            </button>
          </div>
          {error && (
            <div className="code-error">
              <AlertTriangle size={14} />
              {error}
            </div>
          )}
        </section>
        <section className="panel intel-status-panel">
          <div className="panel-heading">
            <div>
              <div className="section-kicker">ACTIVE EXTERNAL PACK</div>
              <h3>{installed ? installed.pack.title : "None installed"}</h3>
            </div>
            {installed && (
              <button className="text-button" type="button" onClick={onClear}>
                <X size={13} />
                Remove
              </button>
            )}
          </div>
          {installed ? (
            <>
              <div
                className={`intel-trust ${
                  installed.trusted && !installed.expired
                    ? "is-trusted"
                    : "is-untrusted"
                }`}
              >
                {installed.trusted && !installed.expired ? (
                  <ShieldCheck size={20} />
                ) : (
                  <ShieldAlert size={20} />
                )}
                <span>
                  <strong>
                    {installed.trusted && !installed.expired
                      ? "Signature trusted"
                      : "Local unverified evidence"}
                  </strong>
                  <small>
                    {installed.signatureValid
                      ? "Ed25519 signature valid"
                      : "No valid signature anchor"}
                  </small>
                </span>
              </div>
              <div className="manifest-table">
                <div>
                  <code>Pack ID</code>
                  <span>{installed.pack.id}</span>
                </div>
                <div>
                  <code>Version</code>
                  <span>{installed.pack.version}</span>
                </div>
                <div>
                  <code>Issued</code>
                  <span>{installed.pack.issuedAt}</span>
                </div>
                <div>
                  <code>Records</code>
                  <span>{installed.records.length.toLocaleString()}</span>
                </div>
              </div>
              {installed.warnings.map((warning) => (
                <div className="intel-warning" key={warning}>
                  <AlertTriangle size={13} />
                  {warning}
                </div>
              ))}
            </>
          ) : (
            <div className="empty-panel">
              <Database size={25} />
              <strong>Built-in intelligence only</strong>
              <span>
                The bundled records remain offline and auditable.
              </span>
            </div>
          )}
        </section>
      </div>
    </div>
  );
}
